import React, { useEffect, useState, useRef } from "react";
import {
  Folder,
  FileText,
  FileSpreadsheet,
  FileImage,
  Search,
  Upload,
  Download,
  Trash2,
  Eye,
  X,
} from "lucide-react";
import { PageHeader, SectionCard } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { folders } from "@/data/mineData";
import { toast } from "sonner";

const folderColors = {
  Operations: "bg-slate-100 text-slate-800 border-slate-200",
  Financials: "bg-amber-100 text-amber-800 border-amber-200",
  Legal: "bg-blue-100 text-blue-800 border-blue-200",
  Geological: "bg-stone-100 text-stone-800 border-stone-200",
  Environmental: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

function getIcon(name, contentType) {
  const ext = name.split(".").pop()?.toLowerCase() || "";
  if (["xlsx", "xls", "csv"].includes(ext)) return FileSpreadsheet;
  if (["png", "jpg", "jpeg", "gif", "webp"].includes(ext) || contentType?.startsWith("image/"))
    return FileImage;
  return FileText;
}

function fmtSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export default function DataRoom() {
  const isAdmin = true;
  const [docs, setDocs] = useState([]);
  const [activeFolder, setActiveFolder] = useState("All");
  const [search, setSearch] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadFolder, setUploadFolder] = useState("Operations");
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef();

  const load = async () => {
    try {
      const params = {};
      if (activeFolder !== "All") params.folder = activeFolder;
      if (search) params.q = search;
      const { data } = await api.get("/documents", { params });
      setDocs(data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFolder, search]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("folder", uploadFolder);
    fd.append("tags", "");
    try {
      await api.post("/documents/upload", fd);
      toast.success(`Uploaded ${file.name}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await api.delete(`/documents/${id}`);
      toast.success("Document removed");
      await load();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const handlePreview = async (doc) => {
    try {
      const resp = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      setPreviewDoc(doc);
      setPreviewUrl(url);
    } catch (e) {
      toast.error("Preview failed");
    }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewDoc(null);
    setPreviewUrl(null);
  };

  const handleDownload = async (doc) => {
    try {
      const resp = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const folderCounts = folders.reduce((acc, f) => {
    acc[f] = docs.filter((d) => d.folder === f).length;
    return acc;
  }, {});

  return (
    <div data-testid="page-data-room">
      <PageHeader eyebrow="Document Data Room" title="Due Diligence Documents" subtitle="Structured access to all transaction documents — searchable, categorised, audit-trailed.">
        {isAdmin && (
          <>
            <select
              value={uploadFolder}
              onChange={(e) => setUploadFolder(e.target.value)}
              data-testid="upload-folder-select"
              className="h-10 px-3 rounded-md border border-slate-300 text-sm bg-white"
            >
              {folders.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleUpload}
              className="hidden"
              data-testid="upload-input"
            />
            <Button
              data-testid="upload-button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="bg-slate-900 hover:bg-slate-800 text-white"
            >
              <Upload size={14} className="mr-2" />
              {uploading ? "Uploading…" : "Upload"}
            </Button>
          </>
        )}
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
        <button
          onClick={() => setActiveFolder("All")}
          data-testid="folder-all"
          className={`text-left bg-white border rounded-lg p-4 shadow-sm hover:shadow-md transition-all ${activeFolder === "All" ? "border-slate-900 ring-2 ring-slate-900/10" : "border-slate-200"}`}
        >
          <div className="flex items-center justify-between">
            <Folder size={18} className="text-slate-700" />
            <span className="text-xs font-semibold text-slate-500">{docs.length}</span>
          </div>
          <div className="font-display text-base font-semibold text-slate-900 mt-3">All Documents</div>
          <div className="text-xs text-slate-500 mt-0.5">Across all folders</div>
        </button>
        {folders.slice(0, 3).map((f) => (
          <button
            key={f}
            onClick={() => setActiveFolder(f)}
            data-testid={`folder-${f.toLowerCase()}`}
            className={`text-left bg-white border rounded-lg p-4 shadow-sm hover:shadow-md transition-all ${activeFolder === f ? "border-slate-900 ring-2 ring-slate-900/10" : "border-slate-200"}`}
          >
            <div className="flex items-center justify-between">
              <Folder size={18} className="text-amber-700" />
              <span className="text-xs font-semibold text-slate-500">{folderCounts[f] || 0}</span>
            </div>
            <div className="font-display text-base font-semibold text-slate-900 mt-3">{f}</div>
            <div className="text-xs text-slate-500 mt-0.5">Documents in folder</div>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {["All", ...folders].map((f) => (
          <button
            key={f}
            onClick={() => setActiveFolder(f)}
            data-testid={`chip-${f.toLowerCase()}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              activeFolder === f
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
            }`}
          >
            {f}
          </button>
        ))}
        <div className="ml-auto relative w-full sm:w-72">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search documents…"
            data-testid="search-input"
            className="pl-9 h-9 border-slate-300"
          />
        </div>
      </div>

      <SectionCard title={`${activeFolder} (${docs.length})`} description="Click a row to preview">
        {docs.length === 0 ? (
          <div className="text-center py-16 text-sm text-slate-500" data-testid="empty-state">
            <FileText size={32} className="mx-auto mb-3 text-slate-300" />
            <p>No documents{search ? ` matching "${search}"` : ""}.</p>
            {isAdmin && <p className="text-xs mt-2">Upload the first one to get started.</p>}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="documents-table">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="py-3 pr-4 font-semibold">Name</th>
                  <th className="py-3 pr-4 font-semibold">Folder</th>
                  <th className="py-3 pr-4 font-semibold">Size</th>
                  <th className="py-3 pr-4 font-semibold">Uploaded</th>
                  <th className="py-3 pr-4 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {docs.map((d) => {
                  const Icon = getIcon(d.name, d.content_type);
                  return (
                    <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`doc-row-${d.id}`}>
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-3 min-w-0">
                          <Icon size={18} className="text-slate-500 flex-shrink-0" />
                          <span className="truncate text-slate-900 font-medium">{d.name}</span>
                        </div>
                      </td>
                      <td className="py-3 pr-4">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold border ${folderColors[d.folder] || folderColors.Operations}`}>
                          {d.folder}
                        </span>
                      </td>
                      <td className="py-3 pr-4 text-slate-600 text-xs">{fmtSize(d.size)}</td>
                      <td className="py-3 pr-4 text-slate-600 text-xs">{new Date(d.created_at).toLocaleDateString()}</td>
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-1 justify-end">
                          <button onClick={() => handlePreview(d)} className="p-1.5 hover:bg-slate-200 rounded text-slate-600 hover:text-slate-900" data-testid={`preview-${d.id}`} aria-label="Preview">
                            <Eye size={14} />
                          </button>
                          <button onClick={() => handleDownload(d)} className="p-1.5 hover:bg-slate-200 rounded text-slate-600 hover:text-slate-900" data-testid={`download-${d.id}`} aria-label="Download">
                            <Download size={14} />
                          </button>
                          {isAdmin && (
                            <button onClick={() => handleDelete(d.id)} className="p-1.5 hover:bg-red-50 rounded text-slate-600 hover:text-red-600" data-testid={`delete-${d.id}`} aria-label="Delete">
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {previewDoc && (
        <div className="fixed inset-0 bg-slate-900/70 z-50 flex items-center justify-center p-4" data-testid="preview-modal">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <div className="font-display font-semibold text-slate-900 truncate pr-4">{previewDoc.name}</div>
              <button onClick={closePreview} className="p-1.5 hover:bg-slate-100 rounded" data-testid="close-preview">
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-auto bg-slate-50">
              {previewDoc.content_type?.startsWith("image/") ? (
                <img src={previewUrl} alt={previewDoc.name} className="max-w-full mx-auto" />
              ) : previewDoc.content_type === "application/pdf" ? (
                <iframe src={previewUrl} title={previewDoc.name} className="w-full h-[75vh]" />
              ) : (
                <div className="p-12 text-center text-sm text-slate-600">
                  <FileText size={32} className="mx-auto mb-3 text-slate-400" />
                  Preview not available for this file type.
                  <div className="mt-4">
                    <Button onClick={() => handleDownload(previewDoc)} className="bg-slate-900 text-white">
                      <Download size={14} className="mr-2" /> Download
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
